import { capitalize } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { builderReset, builderSetShouldEditSetup } from '~/actions/builder';
import { workflowReset } from '~/actions/workflow';
import { PAGE_START, UI_TABLE_PAGE_SIZE } from '~/common/constants/page';
import { User } from '~/components/Shared/User/User';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { useDebouncedState } from '~/eds/src/hooks/useDebouncedState';
import { PageLayout } from '~/eds/src/modules/PageLayout/PageLayout';
import { Table } from '~/eds/src/modules/Table/Table';
import { Action } from '~/eds/src/types/Action';
import { copy } from '~/eds/src/utils/copy';
import { parseDate } from '~/eds/src/utils/parseDate';
import { QueryParamType } from '~/enums/QueryParamType';
import { WorkflowPublishActionType } from '~/enums/WorkflowPublishActionType';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { useTrackSegment } from '~/hooks/useTrackSegment ';
import { api } from '~/redux/api/createApi';
import * as selectors from '~/redux/selectors';
import { useRouting } from '~/routing/useRouting';
import { ColumnSortOrder } from '~/types/Columns';
import { Nullable } from '~/types/Nullable';
import { Option } from '~/types/Option';
import { Workflow } from '~/types/Workflow';
import { sortByDateValue } from '~/utils/array';
import {
  getPageSearchQueryByKey,
  updatePageSearchQuery,
} from '~/utils/searchQuery';
import { getClientInfo, getUserClientInfo } from '~/utils/user';
import { getWorkflowPath } from '~/utils/workflow.utils';

import Setup from '../WorkflowBuilderPage/Setup/Setup';
import { useDuplicateWorkflowModal } from './useDuplicateWorkflowModal';
import { useShareWorkflowModal } from './useShareWorkflowModal';
import { useWorkflowActionModal } from './useWorkflowActionModal';
import { getWorkflowStatusLabels, mapVersionCell } from './utils';

export const WorkflowList = () => {
  const [debouncedSearch, searchText, setSearchText] = useDebouncedState('');
  const [selectedWorkflow, setSelectedWorkflow] = useState<Workflow | null>(
    null,
  );
  const [workflowAction, setWorkflowAction] =
    useState<Nullable<WorkflowPublishActionType | 'DELETE'>>(null);
  const { navigate } = useRouting();
  const { toast } = useToast();
  const currentUser = useCurrentUser();
  const trackSegment = useTrackSegment();
  const dispatch = useDispatch();
  const shouldEditSetup = useSelector(selectors.selectShouldEditSetup);
  const intl = useIntl();
  const workflowStatusLabels = getWorkflowStatusLabels(intl);

  const urlParams = {
    page: getPageSearchQueryByKey([QueryParamType.Page], PAGE_START),
    pageSize: getPageSearchQueryByKey(
      [QueryParamType.PageSize],
      UI_TABLE_PAGE_SIZE,
    ),
    sortedColumn: getPageSearchQueryByKey(
      [QueryParamType.SortedColumn],
      'lastUpdated',
    ),
    sortedOrder: getPageSearchQueryByKey([QueryParamType.SortedOrder], 'desc'),
  };

  const [sortBy, setSortBy] = useState<ColumnSortOrder>({
    id: urlParams.sortedColumn,
    desc: urlParams.sortedOrder === 'desc',
  });

  const [pageSize, setPageSize] = useState(urlParams.pageSize);

  const [pageIndex, setPageIndex] = useState(urlParams.page);

  const handlePagination = ({ pageIndex }: any) => {
    setPageIndex(pageIndex);
    updatePageSearchQuery({ page: pageIndex }, true);
  };

  const handleSearch = (searchText: string) => {
    setSearchText(searchText);
    setPageIndex(PAGE_START);
  };

  const handlePageSize = (value: number) => {
    updatePageSearchQuery({ pageSize: value }, true);
    setPageSize(value);
  };

  const handleUpdate = (state: any, action: any) => {
    switch (action.type) {
      case 'toggleSortBy':
        if (state.sortBy.length) {
          const { id, desc } = state.sortBy[0];

          if (desc) {
            updatePageSearchQuery(
              { sortedOrder: 'desc', sortedColumn: id },
              true,
            );
          } else {
            updatePageSearchQuery(
              { sortedOrder: 'asc', sortedColumn: id },
              true,
            );
          }

          setSortBy(state.sortBy[0]);
        }
        break;
      default:
        return;
    }
  };

  const { data: workflows = [], isFetching } =
    api.endpoints.getWorkflows.useQuery({});

  const [publishWorkflow, { isLoading: isPublishingWorkflow }] =
    api.endpoints.publishWorkflow.useMutation();
  const [unpublishWorkflow, { isLoading: isUnpublishingWorkflow }] =
    api.endpoints.unpublishWorkflow.useMutation();
  const [deleteWorkflow, { isLoading: isDeletingWorkflow }] =
    api.endpoints.deleteWorkflow.useMutation();

  const [totalCount, setTotalCount] = useState(workflows.length);

  const columns = [
    {
      key: 'name',
      cellType: 'link',
      title: intl.formatMessage({
        id: 'WorkflowList.nameColumnTitle',
        defaultMessage: 'Name',
        description: 'Column title for workflow name',
      }),
      mapCellProps: (workflow: Workflow) => ({
        text: workflow.name,
        pathname: `/workflow/builder/${workflow.id}`,
      }),
    },
    {
      key: 'versions',
      cellType: 'menu',
      title: intl.formatMessage({
        id: 'WorkflowList.versionsColumnTitle',
        defaultMessage: 'Versions',
        description: 'Column title for workflow versions',
      }),
      disableSortBy: true,
      disableResizing: true,
      width: 'm',
      mapCellProps: (workflow: Workflow) =>
        mapVersionCell(
          workflow,
          (option: Option<string>) =>
            navigate(`/workflow/builder/${option.value}`),
          intl,
        ),
    },
    {
      key: 'createdBy',
      cellType: 'user',
      title: intl.formatMessage({
        id: 'WorkflowList.createdByColumnTitle',
        defaultMessage: 'Created By',
        description: 'Column title for workflow creator',
      }),
      minWidth: 'm',
      disableResizing: true,
      disableSortBy: true,
      mapCellProps: (workflow: Workflow) => ({
        asyncUser: {
          id: workflow.createdBy,
          render: User,
        },
        mode: 'avatar-name',
      }),
    },
    {
      key: 'lastUpdated',
      cellType: 'userDatetime',
      title: intl.formatMessage({
        id: 'WorkflowList.lastUpdatedColumnTitle',
        defaultMessage: 'Last Updated',
        description: 'Column title for last update date',
      }),
      width: 'm',
      disableResizing: true,
      mapCellProps: ({ modifiedDate, lastModifiedBy }: Workflow) => ({
        asyncUser: {
          id: lastModifiedBy,
          render: User,
        },
        mode: 'name',
        datetime: parseDate(modifiedDate),
        format: 'relative',
      }),
    },
    {
      key: 'status',
      cellType: 'chips',
      title: intl.formatMessage({
        id: 'WorkflowList.statusColumnTitle',
        defaultMessage: 'Status',
        description: 'Column title for workflow status',
      }),
      width: 'm',
      disableResizing: true,
      mapCellProps: (workflow: Workflow) => {
        return {
          chips: [
            {
              text:
                workflowStatusLabels[workflow.status] ??
                capitalize(workflow.status),
              status:
                workflow.status === 'published'
                  ? 'success'
                  : workflow.status === 'unpublished'
                    ? 'warning'
                    : 'info',
            },
          ],
        };
      },
    },
  ];

  const filteredWorkflows = useMemo(() => {
    const pWorkflows = [...workflows];
    switch (sortBy.id) {
      case 'name':
        pWorkflows.sort((a, b) =>
          sortBy.desc
            ? b.name.localeCompare(a.name, undefined, { sensitivity: 'base' })
            : a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
        );
        break;
      case 'lastUpdated':
        pWorkflows.sort(
          sortByDateValue('modifiedDate', sortBy.desc ? 'desc' : 'asc'),
        );
        break;
      case 'status':
        pWorkflows.sort((a, b) =>
          sortBy.desc
            ? b.status.localeCompare(a.status)
            : a.status.localeCompare(b.status),
        );
        break;
    }

    return pWorkflows.filter((workflow) =>
      workflow.name.toLowerCase().includes(debouncedSearch.toLowerCase()),
    );
  }, [workflows, debouncedSearch, sortBy, pageIndex, pageSize]);

  const paginatedWorkflows = useMemo(
    () =>
      filteredWorkflows.slice((pageIndex - 1) * pageSize, pageIndex * pageSize),
    [filteredWorkflows, pageIndex, pageSize],
  );

  useEffect(() => {
    setTotalCount(filteredWorkflows.length);
  }, [filteredWorkflows.length]);

  useEffect(() => {
    updatePageSearchQuery(urlParams);
  }, []);

  const publishUnplishAction = async () => {
    const request =
      workflowAction === WorkflowPublishActionType.Publish
        ? publishWorkflow
        : workflowAction === 'DELETE'
          ? deleteWorkflow
          : unpublishWorkflow;
    try {
      await request({
        id: selectedWorkflow!.id,
      }).unwrap();
      toast({
        message:
          workflowAction === 'DELETE'
            ? intl.formatMessage({
                id: 'WorkflowList.workflowDeletedSuccess',
                defaultMessage: 'Workflow deleted.',
                description: 'Success message when workflow is deleted',
              })
            : selectedWorkflow!.status === 'published'
              ? intl.formatMessage({
                  id: 'WorkflowList.workflowUnpublishedSuccess',
                  defaultMessage: 'Workflow unpublished.',
                  description: 'Success message when workflow is unpublished',
                })
              : intl.formatMessage({
                  id: 'WorkflowList.workflowPublishedSuccess',
                  defaultMessage: 'Workflow published.',
                  description: 'Success message when workflow is published',
                }),
        status: 'success',
      });
    } catch {
      toast({
        message:
          workflowAction === 'DELETE'
            ? intl.formatMessage({
                id: 'WorkflowList.workflowDeleteError',
                defaultMessage: 'Failed to delete the workflow',
                description: 'Error message when workflow deletion fails',
              })
            : intl.formatMessage({
                id: 'WorkflowList.workflowUpdateError',
                defaultMessage: 'Failed to update the workflow',
                description: 'Error message when workflow update fails',
              }),
        status: 'danger',
      });
    } finally {
      setSelectedWorkflow(null);
      setWorkflowAction(null);
      hide();
    }
  };

  const [updateWorkflowModal, show, hide] = useWorkflowActionModal({
    isLoading:
      isPublishingWorkflow || isUnpublishingWorkflow || isDeletingWorkflow,
    workflow: selectedWorkflow,
    action: workflowAction,
    onHide: () => {
      setSelectedWorkflow(null);
      setWorkflowAction(null);
    },
    onConfirm: publishUnplishAction,
  });

  const [shareSettingsModal, showShareSettingsModal] = useShareWorkflowModal({
    workflow: selectedWorkflow,
    onHide: () => setSelectedWorkflow(null),
  });

  const [duplicateWorkflowModal, showDuplicateWorkflowModal] =
    useDuplicateWorkflowModal({
      workflow: selectedWorkflow,
      onHide: () => setSelectedWorkflow(null),
    });

  const handleOnCopyAction = (workflow: Workflow) => {
    const workflowPath = getWorkflowPath({
      permanentLinkId: workflow.permanentLinkId!,
      wssDesignated: workflow.wssDesignated ?? false,
    });
    const linkConstructed = `${window.location.origin}/${currentUser.client_config.sub_domain}${workflowPath}`;
    copy(linkConstructed);
    toast({
      message: intl.formatMessage({
        id: 'WorkflowList.intakeFormLink',
        defaultMessage: 'Intake form link copied to clipboard.',
        description: 'Intake form link copied to clipboard message',
      }),
      status: 'success',
    });
  };

  const getRowActions = (workflow: Workflow) => {
    const actions: Action<string>[] = [
      {
        label: intl.formatMessage({
          id: 'WorkflowList.editAction',
          defaultMessage: 'Edit',
          description: 'Edit workflow action label',
        }),
        value: 'edit',
        onClick: () => {
          navigate(`/workflow/builder/${workflow.id}`);
        },
      },
    ];

    actions.push({
      label: intl.formatMessage({
        id: 'WorkflowList.duplicateAction',
        defaultMessage: 'Make a copy',
        description: 'Duplicate workflow action label',
      }),
      value: 'duplicate',
      onClick: () => {
        setSelectedWorkflow(workflow);
        showDuplicateWorkflowModal();
      },
    });

    if (workflow.permanentLinkId && workflow.isPublished) {
      actions.push({
        label: intl.formatMessage({
          id: 'WorkflowList.copyIntakeFormLinkAction',
          defaultMessage: 'Copy Intake Form Link',
          description: 'Copy intake form link action label',
        }),
        value: 'copy-link',
        onClick: () => handleOnCopyAction(workflow),
      });
    }

    actions.push({
      label:
        workflow.status === 'published'
          ? intl.formatMessage({
              id: 'WorkflowList.unpublishAction',
              defaultMessage: 'Unpublish',
              description: 'Unpublish workflow action label',
            })
          : intl.formatMessage({
              id: 'WorkflowList.publishAction',
              defaultMessage: 'Publish',
              description: 'Publish workflow action label',
            }),
      value: workflow.status === 'published' ? 'unpublish' : 'publish',
      tooltip: workflow.canPublish
        ? ''
        : intl.formatMessage({
            id: 'WorkflowList.yourWorkflowDraft',
            defaultMessage:
              'Your workflow draft requires fixes before it can be published',
            description: 'Tooltip when workflow cannot be published',
          }),
      disabled: workflow.canPublish ? false : true,
      onClick: () => {
        setSelectedWorkflow(workflow);
        setWorkflowAction(
          workflow.status === 'published'
            ? WorkflowPublishActionType.Unpublish
            : WorkflowPublishActionType.Publish,
        );
        show();
      },
    });

    actions.push({
      label: intl.formatMessage({
        id: 'WorkflowList.deleteAction2',
        defaultMessage: 'Delete',
        description: 'Delete workflow action label',
      }),
      value: 'delete',
      onClick: () => {
        setSelectedWorkflow(workflow);
        setWorkflowAction('DELETE');
        show();
      },
    });

    actions.push({
      label: intl.formatMessage({
        id: 'WorkflowList.shareAction',
        defaultMessage: 'Share',
        description: 'Share workflow action label',
      }),
      value: 'share',
      onClick: () => {
        setSelectedWorkflow(workflow);
        showShareSettingsModal();
      },
    });

    return actions;
  };

  function handleCreateWorkflow() {
    dispatch(builderReset());
    dispatch(builderSetShouldEditSetup(true));
    dispatch(workflowReset());
    trackSegment('User Begins New Workflow', {
      groupId: getClientInfo(currentUser),
      userId: getUserClientInfo(currentUser),
    });
  }

  if (shouldEditSetup) {
    return <Setup />;
  }

  return (
    <>
      <PageLayout
        loadingContent={{
          isLoading: isFetching,
          message: intl.formatMessage({
            id: 'WorkflowList.loadingWorkflows',
            defaultMessage: 'Loading workflows...',
            description: 'Loading message for workflows list',
          }),
        }}
        title={intl.formatMessage({
          id: 'WorkflowList.pageTitle',
          defaultMessage: 'Workflow Builder',
          description: 'Page title for workflow builder',
        })}
        actions={
          currentUser?.['is_workflow_admin']
            ? [
                {
                  level: 'primary',
                  text: intl.formatMessage({
                    id: 'WorkflowList.newWorkflowButton',
                    defaultMessage: 'New Workflow',
                    description: 'Button text for creating new workflow',
                  }),
                  onClick: handleCreateWorkflow,
                },
              ]
            : []
        }
      >
        <Table
          name="workflows"
          columns={columns}
          data={paginatedWorkflows}
          state={{
            pageIndex,
            sortBy: [sortBy],
          }}
          options={{
            enableSearch: true,
            enableExportXlsx: false,
            enableManageColumns: false,
            enableSelectRows: false,
            enablePagination: true,
            enablePageSizeSelect: true,
            enableEmptyState: true,
          }}
          onPaginate={handlePagination}
          onPageSizeChange={handlePageSize}
          onUpdate={handleUpdate}
          isLoading={isFetching}
          onSearch={handleSearch}
          searchText={searchText}
          totalCount={totalCount}
          reactTableOptions={{
            autoResetSortBy: false,
            disableSortRemove: true,
            manualSortBy: true,
          }}
          getRowActions={getRowActions}
        />
        {updateWorkflowModal}
        {shareSettingsModal}
        {duplicateWorkflowModal}
      </PageLayout>
    </>
  );
};
